// =============================================
// uploadQueue — app-wide background queue for document uploads
// =============================================
// Upload Docs hands its files to this module-level queue instead of processing
// them inside the page component. The queue lives for the life of the browser
// tab, so processing (convert → split → compress → auto-fill extraction → blob
// store → dms_documents row) carries on while the user works in Process
// Docs/Receipts or any other service, and the Upload Docs page shows live
// progress again whenever it is reopened.
//
// It also survives the tab itself going away (crash / reload / close). Every
// waiting file is kept in IndexedDB (uploadQueueStore.ts) until it is completely
// saved, and restoreSavedUploads() puts it back in the queue the next time the app
// opens. A file split into many documents is finished, never restarted: each
// document records which pages of the original it holds (migration 120), the server
// is asked which are already there, and only the rest are saved. While work is
// pending the browser's own "Leave site?" prompt is still armed.
//
// Each job carries the context it was added with (owner, tab, engine, notes),
// so changing those on the page afterwards never alters files already queued.
// =============================================

import { useSyncExternalStore } from 'react';
import { PDFDocument } from 'pdf-lib';
import { compressBytesToBase64 } from './pdfBytes';
import { addRow, isApiReady } from '../../db-viewer/data/dataAccess';
import { storeDocBlob, isServerKey } from './docStorage';
import { getUploadedPieces } from '../../../lib/serverDocStore';
import { handleSessionExpired } from '../../../lib/authGuard';
import { saveUpload, forgetUpload, loadUploads, SavedUpload } from './uploadQueueStore';
import { extractFilingFields, resolveDocuTypeId, ExtractEngine } from './documentExtract';
import { sha256Hex, buildContentTokens, contentSignatureFromTokens } from './documentFingerprint';
import { EMAIL_EXTENSIONS, parseEmailFile, emailPartToFile } from '../../../lib/emailStore';
import { readPdfPageTexts } from './pdfTextExtractor';
import { decideDocDate, readHandwrittenDate, handwritingCheckAllowed } from './handwrittenDate';
import { planDocumentSplit, SplitPlan } from './pageGrouping';
import apiClient from '../../../lib/api/apiClient';
import type { ParsedReceiptData } from './receiptParser';

declare global {
  interface Window {
    CompressionStream: any;
  }
  var CompressionStream: {
    new(format: string): {
      readable: ReadableStream<Uint8Array>;
      writable: WritableStream<Uint8Array>;
    };
  } | undefined;
}

// 'receipts-auto' = read the pages and keep each document's pages together: a file
// that mixes single-page receipts with multi-page invoices (see pageGrouping.ts).
export type UploadMode = 'receipts-docs' | 'receipts-separate' | 'receipts-auto';

/** Everything a job needs that the Upload Docs page knew when the file was added. */
export interface UploadContext {
  mode: UploadMode;                 // as-is vs. one document per PDF page vs. auto-separated
  reviewSplit?: boolean;            // auto: wait for the user to check the split before saving
  engine: ExtractEngine;            // auto-fill extraction engine
  accountOwnerId: number;
  docUserId: number;
  docOwnerType: string | null;
  docOwnerId: number | null;
  createdBy: number | null;
  notes: string | null;
  docuTypes: Record<string, any>[]; // acctg_docu_type rows, to resolve the type hint
  origin?: string;                  // the upload window it was added from (screen + client); absent on older saved jobs
}

export interface UploadJob {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  // 'review' = auto-separate has a suggested split waiting for the user to check it
  status: 'queued' | 'uploading' | 'review' | 'done' | 'error';
  progress: number;
  errorMsg?: string;
  addedAt: string;
  storedSize?: number;
  pagesCreated?: number;
  alreadyUploaded?: number;         // split modes: pages found on the server and not saved again
  resumed?: boolean;                // picked up again after a reload / crash
  split?: SplitPlan;                // auto: the suggested split (while status is 'review')
  confirmedGroups?: number[][];     // auto: the split the user approved
  docsCreated?: number;             // auto: how many documents the file became…
  pagesTotal?: number;              // …out of how many pages
  convertedFrom?: string;
  emailDocs?: number;               // an email: how many documents it produced
  emailSkipped?: string[];          // an email: attachments that could not be filed
  ctx?: UploadContext;              // absent on files rejected by validation
  // Files rejected by validation have no ctx — they still remember their window.
  source?: { mode: UploadMode; origin?: string };
}

/** Does this job belong to the upload window `mode` + `origin`? Jobs saved before
 *  windows were recorded match any window of the same tab. */
export function jobInWindow(j: UploadJob, mode: UploadMode, origin: string): boolean {
  const src = j.ctx ? { mode: j.ctx.mode, origin: j.ctx.origin } : j.source;
  if (!src) return false;
  return src.mode === mode && (src.origin == null || src.origin === origin);
}

/** Fired on `window` each time a job finishes, so open screens can reload dms_documents. */
export const DOCS_UPLOADED_EVENT = 'akt:dms-docs-uploaded';

// Same signal for OTHER tabs/windows of this browser (e.g. Upload Docs in one tab,
// Process Docs/Receipts in another). Absent in old browsers and in tests.
const docsChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('akt-dms-docs') : null;

function announceDocsUploaded(): void {
  window.dispatchEvent(new Event(DOCS_UPLOADED_EVENT));
  try { docsChannel?.postMessage('uploaded'); } catch { /* other tabs catch up on focus */ }
}

/**
 * Run `onUploaded` whenever an upload finishes — in this tab or in another tab of
 * the same browser. Returns an unsubscribe.
 */
export function subscribeDocsUploaded(onUploaded: () => void): () => void {
  const listener = () => onUploaded();
  window.addEventListener(DOCS_UPLOADED_EVENT, listener);
  docsChannel?.addEventListener('message', listener);
  return () => {
    window.removeEventListener(DOCS_UPLOADED_EVENT, listener);
    docsChannel?.removeEventListener('message', listener);
  };
}

export const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg'];

export function getFileExtension(filename: string): string {
  return '.' + (filename.split('.').pop()?.toLowerCase() || '');
}

function nowTimestamp(): string {
  return new Date().toISOString().replace('T', ' ').substring(0, 19);
}

// =============================================
// Store
// =============================================
let jobs: UploadJob[] = [];
let running = false;
const listeners = new Set<() => void>();

function emit() { listeners.forEach(l => l()); }
function patchJob(id: string, patch: Partial<UploadJob>) {
  jobs = jobs.map(j => (j.id === id ? { ...j, ...patch } : j));
  emit();
}

export function subscribeUploads(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function getUploadJobs(): UploadJob[] { return jobs; }

/** Live view of the queue for a component. */
export function useUploadJobs(): UploadJob[] {
  return useSyncExternalStore(subscribeUploads, getUploadJobs);
}

/** Is anything still waiting or in progress? A split awaiting its check counts: nothing is saved yet. */
export function hasActiveUploads(): boolean {
  return jobs.some(j => j.status === 'queued' || j.status === 'uploading' || j.status === 'review');
}

// =============================================
// Surviving a reload / crash (see uploadQueueStore.ts)
// =============================================

// The login the queue is working for — read the same way when a file is kept and when
// it is restored, so a file is only ever resumed by the login that added it.
function sessionUserId(): string | null {
  try {
    const id = JSON.parse(localStorage.getItem('akt_session_user') || 'null')?.id;
    return id != null ? String(id) : null;
  } catch {
    return null;
  }
}

// A file must only ever be worked on by ONE tab: two tabs resuming the same file would
// each save the pages that are left. The tab that has a job holds a Web Lock named after
// it for as long as it has it; the browser drops the lock by itself when that tab closes
// or crashes, which is exactly when another tab may take the file over.
const heldLocks = new Map<string, () => void>();

function holdJobLock(id: string): Promise<boolean> {
  const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : null;
  if (!locks?.request || heldLocks.has(id)) return Promise.resolve(true);
  return new Promise<boolean>(resolve => {
    locks.request(`akt-upload-job-${id}`, { ifAvailable: true }, (lock: any) => {
      if (!lock) { resolve(false); return undefined; }           // another tab has this file
      return new Promise<void>(release => { heldLocks.set(id, release); resolve(true); });
    }).catch(() => resolve(true));
  });
}

function releaseJobLock(id: string): void {
  heldLocks.get(id)?.();
  heldLocks.delete(id);
}

// Store writes run strictly one after another, so "keep" can never land after the
// "forget" that follows it.
let storeChain: Promise<unknown> = Promise.resolve();
function storeStep(step: () => Promise<unknown>): void {
  storeChain = storeChain.then(step, step);
}

function forget(id: string): void {
  storeStep(async () => { await forgetUpload(id); releaseJobLock(id); });
}

// Keep this job's file until it is fully saved. A finished job is forgotten instead.
function remember(id: string): void {
  const job = jobs.find(j => j.id === id);
  const userId = sessionUserId();
  if (!job || !job.ctx || !userId) return;
  if (job.status === 'done') { forget(id); return; }
  const record: SavedUpload = {
    id: job.id, userId, file: job.file, name: job.name, size: job.size, type: job.type,
    addedAt: job.addedAt, convertedFrom: job.convertedFrom, ctx: job.ctx,
    // 'uploading' is kept as 'queued': after a crash it has to start (and skip ahead) again.
    status: job.status === 'review' || job.status === 'error' ? job.status : 'queued',
    errorMsg: job.errorMsg, split: job.split, confirmedGroups: job.confirmedGroups,
    savedAt: Date.now(),
  };
  storeStep(async () => { await holdJobLock(id); await saveUpload(record); });
}

let restored = false;

/**
 * Put back the files this login left unfinished (tab crashed, closed or reloaded
 * mid-upload) and carry on. Safe to call on every screen — it runs once per page load.
 */
export async function restoreSavedUploads(): Promise<void> {
  if (restored) return;
  const userId = sessionUserId();
  if (!userId) return;               // not logged in yet — try again on the next call
  restored = true;
  await storeChain;
  const fresh: SavedUpload[] = [];
  for (const r of await loadUploads(userId)) {
    if (jobs.some(j => j.id === r.id)) continue;
    if (await holdJobLock(r.id)) fresh.push(r);     // not if another open tab is on it
  }
  if (fresh.length === 0) return;
  jobs = [...jobs, ...fresh.map((r): UploadJob => ({
    id: r.id, file: r.file, name: r.name, size: r.size, type: r.type,
    status: r.status, progress: 0, errorMsg: r.errorMsg, addedAt: r.addedAt,
    convertedFrom: r.convertedFrom, split: r.split, confirmedGroups: r.confirmedGroups,
    ctx: r.ctx as UploadContext, resumed: true,
  }))];
  emit();
  void run();
}

/** Add files to the queue; queued ones start processing straight away. */
export function addUploadJobs(newJobs: UploadJob[]): void {
  if (newJobs.length === 0) return;
  jobs = [...jobs, ...newJobs];
  emit();
  newJobs.forEach(j => { if (j.status === 'queued') remember(j.id); });
  void run();
}

/** The user checked an auto-separated file: save it as these documents (0-based page indexes). */
export function confirmUploadSplit(id: string, groups: number[][]): void {
  if (!jobs.some(j => j.id === id && j.status === 'review')) return;
  patchJob(id, { status: 'queued', confirmedGroups: groups, split: undefined });
  remember(id);
  void run();
}

/** Try a stopped file again. It continues where it stopped — saved pages are not uploaded twice. */
export function retryUploadJob(id: string): void {
  if (!jobs.some(j => j.id === id && j.status === 'error' && j.ctx)) return;
  patchJob(id, { status: 'queued', progress: 0, errorMsg: undefined });
  remember(id);
  void run();
}

/** Remove a file from the list. The one being processed right now can't be pulled back. */
export function removeUploadJob(id: string): void {
  jobs.forEach(j => { if (j.id === id && j.status !== 'uploading') forget(j.id); });
  jobs = jobs.filter(j => j.id !== id || j.status === 'uploading');
  emit();
}

/** Clear the list — waiting files are dropped, the one in progress finishes.
 *  `inScope` limits the clear to one upload window (other jobs are kept). */
export function clearUploadJobs(inScope: (j: UploadJob) => boolean = () => true): void {
  jobs.forEach(j => { if (inScope(j) && j.status !== 'uploading') forget(j.id); });
  jobs = jobs.filter(j => !inScope(j) || j.status === 'uploading');
  emit();
}

// Closing or reloading the tab pauses the queue until the app is opened again.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (!hasActiveUploads()) return;
    // An expired session is already on its way to the sign-in screen — don't stand in the way.
    try { if (sessionStorage.getItem('akt_session_expired') === '1') return; } catch { /* ask as usual */ }
    e.preventDefault();
    e.returnValue = '';
  });
}

// =============================================
// Image → PDF conversion (pdf-lib)
// =============================================
async function convertImageToPdfBytes(file: File): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  const imageBytes = new Uint8Array(await file.arrayBuffer());
  const ext = getFileExtension(file.name);

  const image = ext === '.png'
    ? await pdfDoc.embedPng(imageBytes)
    : await pdfDoc.embedJpg(imageBytes);

  const page = pdfDoc.addPage([image.width, image.height]);
  page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });

  return pdfDoc.save();
}

// =============================================
// PDF page splitting (pdf-lib)
// =============================================
// One PDF holding these pages of the source (0-based indexes, in the order given).
// Cut one at a time, as each is about to be saved — a long scan is never held in
// memory as hundreds of separate PDFs.
async function cutPdfPages(srcDoc: PDFDocument, pageIndexes: number[]): Promise<Uint8Array> {
  const newDoc = await PDFDocument.create();
  const copied = await newDoc.copyPages(srcDoc, pageIndexes);
  copied.forEach(p => newDoc.addPage(p));
  return newDoc.save();
}

// =============================================
// Trying again
// =============================================
const RETRY_WAITS_MS = [2000, 6000, 15000];

// Run a server call, and on a failure that may pass (a 5xx, a dropped connection that
// was not queued offline) wait and try again. A 401 is never retried: the session is over.
async function withRetry<T>(call: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await call();
    } catch (err: any) {
      const status = Number(err?.status) || 0;
      const giveUp = status === 401 || (status >= 400 && status < 500 && status !== 408 && status !== 429);
      if (giveUp || attempt >= RETRY_WAITS_MS.length) throw err;
      await new Promise(resolve => setTimeout(resolve, RETRY_WAITS_MS[attempt]));
    }
  }
}

// Read every page and suggest which pages belong together. `progress` gets 0–1.
async function planSplit(pdfBytes: Uint8Array, progress: (fraction: number) => void): Promise<SplitPlan> {
  const pages = await readPdfPageTexts(pdfBytes, (done, total) => progress(done / total));
  return planDocumentSplit(pages);
}

// =============================================
// Processing: convert → split → compress → extract → save
// =============================================

// Run the chosen extraction engine on one file's bytes → the Filing-field
// columns to bake into its dms_documents insert. Best-effort: a failed or blank
// extraction just leaves fields empty (the Filing tab is the review surface).
// Owner is NEVER set here — it comes only from the owner selector on the page.
async function buildExtracted(
  ctx: UploadContext, rawBytes: Uint8Array, gzipBase64: string, fname: string
): Promise<{ fields: Record<string, any>; read: ParsedReceiptData | null }> {
  try {
    const parsed = await extractFilingFields({
      engine: ctx.engine, gzipBase64, rawBytes, mediaType: 'application/pdf', filename: fname,
    });
    const out: Record<string, any> = {};
    if (parsed.partyName) out.party_name = parsed.partyName;
    if (parsed.invoiceRef) out.invoice_ref = parsed.invoiceRef;
    // Date: a date written on the document by hand wins over the printed one; where the
    // two differ it is filled in but left for the user to approve (see handwrittenDate.ts).
    // Reading handwriting needs the AI reader, so the free-only engine skips it.
    const handwritten = handwritingCheckAllowed(ctx.engine) ? await readHandwrittenDate(gzipBase64) : null;
    const decided = decideDocDate(parsed.dateOfDocument, handwritten?.date, parsed.dateSource || 'text');
    if (decided.date) out.date_of_document = decided.date;
    if (decided.source) out.date_source = decided.source;
    if (parsed.dateOfDocument) out.printed_date = parsed.dateOfDocument;
    if (handwritten) {
      out.handwritten_checked = 1;
      if (handwritten.date) out.handwritten_date = handwritten.date;
      if (decided.confirmNeeded) out.date_confirm_needed = 1;
    }
    // Keep the date EXACTLY as printed: Process Docs re-reads it against the vendor's
    // learned date layout before the guess above is trusted.
    if (parsed.rawDateString) out.raw_date_string = String(parsed.rawDateString).slice(0, 40);
    if (parsed.paymentMethod) out.payment_method = parsed.paymentMethod;
    if (parsed.totalAmount) {
      const n = parseFloat(parsed.totalAmount);
      if (!isNaN(n)) out.total_amount = n;
    }
    // Currency is NOT taken from the read: it was often wrong (GBP / EUR on Canadian
    // receipts). Process Docs defaults it to the owner's base currency instead.
    if (parsed.purpose) out.purpose = parsed.purpose;
    const dtId = resolveDocuTypeId(parsed.docTypeHint, ctx.docuTypes, parsed.rawText);
    if (dtId != null) out.docu_type_id = dtId;
    // Content fingerprint for duplicate detection — over the document's text
    // PLUS the extracted Filing fields (so it still works for AI-only extracts
    // that return no rawText). Best-effort; blank signature is fine.
    const sig = contentSignatureFromTokens(buildContentTokens({
      rawText: parsed.rawText,
      partyName: parsed.partyName,
      totalAmount: parsed.totalAmount,
      dateOfDocument: parsed.dateOfDocument,
      invoiceRef: parsed.invoiceRef,
      currency: parsed.currency,
    }));
    if (sig) out.content_minhash = sig;
    return { fields: out, read: parsed };
  } catch {
    return { fields: {}, read: null };
  }
}

// Add the party just read off an uploaded document to the sitewide business directory
// (as 'unverified' — nobody has checked it yet; filing confirms it). Public business facts
// only, never the document text. Fire and forget: an upload never waits on or fails for it.
function captureParty(saved: Record<string, any>, read: ParsedReceiptData | null): void {
  const documentId = Number(saved?.document_id);
  if (!read || !read.partyName || !documentId || saved.__pendingSync || !isApiReady()) return;
  const data: Record<string, any> = { partyName: read.partyName };
  for (const [k, v] of Object.entries(read)) if (k.startsWith('business') && v) data[k] = v;
  apiClient.post('/directory/capture', { document_id: documentId, data }).catch(() => { /* best-effort */ });
}

// SHA-256 of the stored bytes — exact-duplicate signal. Never throws to the
// upload (a hashing failure just leaves file_hash null).
async function fileHashOf(bytes: Uint8Array): Promise<string | null> {
  try { return await sha256Hex(bytes); } catch { return null; }
}

// Compress, extract, store the bytes and insert one dms_documents row.
async function saveDocument(
  ctx: UploadContext, bytes: Uint8Array,
  row: {
    filename: string; original_filename: string; file_size_original: number; page_count: number | null;
    // Split uploads: the original file and which of its pages this document holds (migration 120).
    source_file_hash?: string | null; source_page_from?: number; source_page_to?: number; source_pages_total?: number;
  },
): Promise<number> {
  const { base64, compressedSize } = await compressBytesToBase64(bytes);
  const now = nowTimestamp();
  // Auto-extract Filing fields and bake them into the SAME insert so the
  // doc keeps created_at === updated_at (still listed as pending to file).
  const { fields: extracted, read } = await buildExtracted(ctx, bytes, base64, row.original_filename);
  const fileHash = await fileHashOf(bytes);
  // Push bytes to the blob store (server-first → /mnt/blobs) the moment
  // processing finishes, so a cleared browser never loses a pending file.
  // When the bytes are durably on the SERVER, don't also store them inline
  // (keeps dms_documents lean); keep inline base64 only on the idb fallback.
  const blobKey = await storeDocBlob(base64, ctx.accountOwnerId);
  const inlineBytes = isServerKey(blobKey) ? null : base64;

  const saved = await withRetry(() => addRow('dms_documents', {
    account_owner_id: ctx.accountOwnerId,
    user_id: ctx.docUserId,
    dms_folders_id: null,
    dms_folders_lvl1_id: null,
    dms_folders_lvl2_id: null,
    filename: row.filename,
    original_filename: row.original_filename,
    ...extracted,
    file_hash: fileHash,
    ...(row.source_file_hash ? {
      source_file_hash: row.source_file_hash,
      source_page_from: row.source_page_from,
      source_page_to: row.source_page_to,
      source_pages_total: row.source_pages_total,
    } : {}),
    file_base64: inlineBytes,
    file_path: blobKey,
    file_size_original: row.file_size_original,
    file_size_stored: compressedSize,
    mime_type: 'application/pdf',
    page_count: row.page_count,
    upload_source: ctx.mode,
    upload_date: now,
    status: 'Review',
    owner_type: ctx.docOwnerType,
    owner_id: ctx.docOwnerId,
    notes: ctx.notes,
    is_active: 1,
    is_deleted: 0,
    created_by: ctx.createdBy,
    created_at: now,
    updated_at: now,
  }, 'document_id'));
  captureParty(saved, read);
  return compressedSize;
}

// Store ONE PDF / image file as its document(s): convert → (split) → save.
// `progress` reports 20–90 for this file; the caller maps it onto the job's bar.
// `groups` (auto-separate only) = a split already decided; without it the pages are read here.
async function processFile(
  file: File, ctx: UploadContext, progress: (pct: number) => void, groups?: number[][],
): Promise<{ storedSize: number; pagesCreated?: number; docsCreated?: number; pagesTotal?: number; alreadyUploaded?: number }> {
  const isImage = IMAGE_EXTENSIONS.includes(getFileExtension(file.name));
  const baseName = file.name.replace(/\.[^.]+$/, '');

  // Step 1: Get PDF bytes (convert image if needed)
  progress(20);
  const fileBytes = new Uint8Array(await file.arrayBuffer());
  const pdfBytes = isImage ? await convertImageToPdfBytes(file) : fileBytes;

  const separate = ctx.mode === 'receipts-separate';
  if (separate || (ctx.mode === 'receipts-auto' && !isImage)) {
    // --- Split: one document per page (separate) or per group of pages that belong
    //     together (auto-separate) ---
    const srcDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
    const pagesTotal = srcDoc.getPageCount();
    const pageGroups = separate
      ? Array.from({ length: pagesTotal }, (_, i) => [i])
      : groups ?? (await planSplit(pdfBytes, f => progress(20 + Math.round(f * 25)))).groups;
    const base = separate ? 30 : 45;
    progress(base);
    // The whole file is one document → store the file itself, untouched.
    const whole = pageGroups.length === 1 && pageGroups[0].length === pagesTotal;

    // Which of these are on the server already? An upload that was cut short (tab
    // crashed or closed, connection lost) is finished from where it stopped, and the
    // same file added a second time saves nothing twice. Keyed on the ORIGINAL file's
    // bytes, so it holds across reloads, browsers and devices.
    const sourceHash = await fileHashOf(fileBytes);
    const onServer = new Set<string>();
    if (sourceHash) {
      const pieces = await withRetry(() => getUploadedPieces(sourceHash, ctx.mode, ctx.accountOwnerId));
      pieces.forEach(p => onServer.add(`${p.from}-${p.to}`));
    }

    let totalStoredSize = 0;
    let saved = 0;
    let pagesSaved = 0;
    let alreadyUploaded = 0;
    for (let i = 0; i < pageGroups.length; i++) {
      const group = pageGroups[i];
      const first = group[0] + 1;
      const last = group[group.length - 1] + 1;
      if (onServer.has(`${first}-${last}`)) {
        alreadyUploaded += group.length;
      } else {
        try {
          const bytes = whole ? pdfBytes : await cutPdfPages(srcDoc, group);
          totalStoredSize += await saveDocument(ctx, bytes, {
            filename: whole
              ? (isImage ? `${baseName}.pdf` : file.name)
              // The source pages stay readable in the name: scan_page3.pdf (separate),
              // scan_p3-5.pdf = pages 3 to 5 (auto-separate).
              : separate ? `${baseName}_page${first}.pdf` : `${baseName}_p${first}${last > first ? `-${last}` : ''}.pdf`,
            original_filename: file.name,
            file_size_original: bytes.length,
            page_count: group.length,
            source_file_hash: sourceHash,
            source_page_from: first,
            source_page_to: last,
            source_pages_total: pagesTotal,
          });
          saved++;
          pagesSaved += group.length;
        } catch (err: any) {
          // Say exactly how far it got: everything before this is safely on the server.
          const done = pagesSaved + alreadyUploaded;
          const e = new Error(
            `Stopped at page ${first} of ${pagesTotal} (${err?.message || 'upload failed'}). `
            + `${done} ${done === 1 ? 'page is' : 'pages are'} saved — press Retry to continue from here.`);
          (e as any).status = err?.status;
          throw e;
        }
      }
      progress(base + Math.round(((i + 1) / pageGroups.length) * (90 - base)));
    }
    return separate
      ? { storedSize: totalStoredSize, pagesCreated: saved, pagesTotal, alreadyUploaded }
      : { storedSize: totalStoredSize, docsCreated: saved, pagesTotal: pagesSaved, alreadyUploaded };
  }

  // --- Upload as-is (no separation) ---
  progress(40);
  let pageCount: number | null = 1;
  if (!isImage) {
    try {
      pageCount = (await PDFDocument.load(pdfBytes, { ignoreEncryption: true })).getPageCount();
    } catch { pageCount = null; }
  }
  progress(70);
  const storedSize = await saveDocument(ctx, pdfBytes, {
    filename: isImage ? `${baseName}.pdf` : file.name,
    original_filename: file.name,
    file_size_original: file.size,
    page_count: pageCount,
  });
  return { storedSize };
}

// An email (.eml / .msg): the server reads it and hands back what it carries. On the
// plain upload tab that is ONE document — the message as the first page(s), then its
// PDF/image attachments; on the "Separate" tab the message and each attachment come back
// on their own and are split page by page like any PDF. Where a document came from is
// kept in its notes.
async function processEmailJob(job: UploadJob, ctx: UploadContext): Promise<void> {
  // Kept whole (message first, attachments after it) unless the tab separates pages.
  // Auto-separate takes the message and each attachment on their own and splits each
  // as suggested — an email's attachments are not held back for a check.
  const email = await parseEmailFile(job.file, ctx.mode === 'receipts-docs');
  const skipped = email.skipped.map(s => s.filename);
  if (email.parts.length === 0) {
    throw new Error(skipped.length > 0
      ? `Nothing in this email can be filed — only PDF, PNG and JPEG are supported (found: ${skipped.join(', ')}).`
      : 'This email has no text and no attachments to file.');
  }
  const emailCtx: UploadContext = { ...ctx, notes: [ctx.notes, email.note].filter(Boolean).join(' — ') };
  let storedSize = 0;
  let saved = 0;
  for (let i = 0; i < email.parts.length; i++) {
    const out = await processFile(emailPartToFile(email.parts[i]), emailCtx, (pct) => {
      patchJob(job.id, { progress: 15 + Math.round(((i + (pct - 20) / 70) / email.parts.length) * 80) });
    });
    storedSize += out.storedSize;
    saved += out.pagesCreated ?? out.docsCreated ?? 1;
  }
  patchJob(job.id, { status: 'done', progress: 100, storedSize, emailDocs: saved, emailSkipped: skipped });
}

async function processJob(job: UploadJob, ctx: UploadContext): Promise<void> {
  patchJob(job.id, { status: 'uploading', progress: 10 });
  try {
    if (EMAIL_EXTENSIONS.includes(getFileExtension(job.file.name))) {
      await processEmailJob(job, ctx);
      return;
    }
    const progress = (pct: number) => patchJob(job.id, { progress: pct });
    let groups = job.confirmedGroups;
    const isPdf = getFileExtension(job.file.name) === '.pdf';
    if (ctx.mode === 'receipts-auto' && ctx.reviewSplit && isPdf && !groups) {
      // Suggest a split, then hand the job to the user. The queue moves on to the
      // next file; confirmUploadSplit() brings this one back with the final groups.
      const plan = await planSplit(
        new Uint8Array(await job.file.arrayBuffer()), f => progress(10 + Math.round(f * 85)),
      );
      if (plan.pageCount > 1) {
        patchJob(job.id, { status: 'review', progress: 0, split: plan });
        remember(job.id);
        return;
      }
      groups = plan.groups;
    }
    const out = await processFile(job.file, ctx, progress, groups);
    patchJob(job.id, { status: 'done', progress: 100, ...out });
  } catch (err: any) {
    if (Number(err?.status) === 401) {
      // The session ended mid-file. The file stays kept as waiting and carries on from
      // the page it reached once the user has signed in again.
      patchJob(job.id, { status: 'queued', progress: 0 });
      handleSessionExpired();
      throw err;
    }
    patchJob(job.id, { status: 'error', progress: 0, errorMsg: err?.message || 'Upload failed' });
  } finally {
    remember(job.id);   // done → forgotten; stopped → kept, with why
  }
}

// One file at a time, in the order added, until nothing is waiting.
async function run(): Promise<void> {
  if (running) return;
  running = true;
  try {
    for (;;) {
      const job = jobs.find(j => j.status === 'queued' && j.ctx);
      if (!job || !job.ctx) break;
      await processJob(job, job.ctx);
      announceDocsUploaded();
    }
  } catch {
    // Session expired: the page is on its way to the sign-in screen. What is left stays
    // kept and resumes after sign-in.
  } finally {
    running = false;
  }
}
